import React, { useState, useEffect, useRef } from "react";
import {
  TableContainer,
  Paper,
  Typography,
  Box,
  PaginationItem,
  Button,
  Grid,
} from "@mui/material";
import { api, get } from "../../../../utils/api";
import { styled } from "@mui/material/styles";
import DateRangePickerComponent from "../../Roles and Permissions/Filters/Date";
import PaginationBox from "../../../../Components/Masters/Pagination";
import DeleteDialog from "../../../../Components/Masters/DeleteDialog";
import ColumnHeadings from "../../../../Components/Masters/ColumnHeadings";
import MenuActions from "../../../../Components/Masters/MenuActions";
import TableContent from "../../../../Components/Masters/TableContent";
import FilterDrawer from "../../../../Components/Masters/FilterDrawer";
import TopMenu from "../../../../Components/Masters/TopMenu";
import MessagePopup from "../../../../Components/Masters/MessagePopup";
import TableHeading from "../../../../Components/Masters/TableHeading";
import MenuIcon from "../../../../Components/Masters/MenuIcon";
import MultiSelect from "../../Roles and Permissions/Filters/MultiSelect";
import AddLeasedAssets from "./AddLeasedAssets";
import ViewLeasedAssets from "./ViewLeasedAssets";
import NoDataFound from "../../../../Components/Masters/NoDataFound";
import MainDashboardLayout from "../../../../Layout/MainDashboardLayout";
import MastersSidebar from "../../../../Layout/MastersSidebar";
import Unauthorized from "../../../../Components/Unauthorized/Unauthorized";

const StyledPaginationItem = styled(PaginationItem)(({ theme }) => ({
  "&.Mui-selected": {
    backgroundColor: "green",
    color: "#fff",
    fontWeight: "bold",
  },
  "&:hover": {
    backgroundColor: "#ededed",
  },
}));

const formInputData = [
  {
    fieldName: "assetId",
    title: "Asset ID",
    placeholder: "e.g. LA-001",
  },
  {
    fieldName: "locationCountry",
    title: "Country",
    placeholder: "Tap to Select",
    type: "select",
  },

  {
    fieldName: "typeOfAsset",
    title: "Type of Asset",
    placeholder: "Tap to Select",
    type: "select",
  },
  {
    fieldName: "leaseStartDate",
    title: "Lease start date",
  },
  {
    fieldName: "leaseEndDate",
    title: "Lease end date",
  },
  {
    fieldName: "assetSize",
    title: "Asset size",
    placeholder: "Numeric size",
  },
  {
    fieldName: "assetSizeUnit",
    title: "Asset size unit",
  },
];

const viewInputData = [
  ...formInputData.filter((f) => f.fieldName !== "assetSizeUnit"),
  {
    fieldName: "leasedType",
    title: "Leased type",
  },
];

const LeasedAssets = () => {
  const [leasedType, setLeasedType] = useState("upstream");
  const [leasedTypeButton, setLeasedTypeButton] = useState("Upstream");
  const [data, setData] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    key: "createdAt",
    direction: "desc",
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedData, setSelectedData] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [addData, setAddData] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState({
    Date: false,
    Location: false,
  });
  const [viewData, setViewData] = useState(false);
  const [element, setElement] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState("Date");
  const [drawerFilterOpen, setDrawerFilterOpen] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [snackOpen, setsnackOpen] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const [uniqueValues, setUniqueValues] = useState({});
  const [errors, setErrors] = useState({
    Date: false,
    Location: false,
  });
  const [isFiltersApplied, setIsFiltersApplied] = useState(false);
  const [selectedFields, setSelectedFields] = useState({
  });
  const [tempFilter, setTempFilter] = useState({
    startDate: null,
    endDate: null,
  });

  const filters = ["Date", ];

  const filtersToApply = {
    startDate: tempFilter?.startDate
      ? tempFilter?.startDate.format("YYYY-MM-DD")
      : "",
    endDate: tempFilter?.endDate
      ? tempFilter?.endDate.format("YYYY-MM-DD")
      : "",
  };

  const fetchUniqueValues = async () => {
    try {
      const uniqueQueryParams = new URLSearchParams({
        leasedType,
        limit: "10",
        page: "1",
        sortField: "createdAt",
        sortOrder: "desc",
      }).toString();
      const response = await get(`/master/leasedAssets?${uniqueQueryParams}`);
      const list = response.data?.data || [];
      const countries = [
        ...new Set(list?.map((el) => el.locationCountry).filter(Boolean)),
      ];
      return { locationCountry: countries };
    } catch (error) {
      return {};
    }
  };

  const updatePage = () => {
    if (page > 1 && data.length === 1) {
      setPage(page - 1);
    }
    fetchData();
  };

  const fetchData = async (
    filtersArg = isFiltersApplied ? filtersToApply : {}
  ) => {
    try {
      const queryParams = new URLSearchParams({
        leasedType,
        page: String(page),
        limit: String(rowsPerPage),
        sortField: sortConfig.key,
        sortOrder: sortConfig.direction,
        ...Object.fromEntries(
          Object.entries(filtersArg).filter(
            ([, v]) => v !== "" && v !== undefined && v !== null
          )
        ),
      }).toString();
      const response = await get(`/master/leasedAssets?${queryParams}`);
      setData(response.data.data);
      setTotalPages(response.data.totalPages);
      const uniqueVals = await fetchUniqueValues();
      setUniqueValues(uniqueVals);
    } catch (error) {
      console.error("Error fetching leased assets:", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage, sortConfig, leasedType]);

  const handleLeasedTypeToggle = (label) => {
    setLeasedTypeButton(label);
    setLeasedType(label === "Upstream" ? "upstream" : "downstream");
    setPage(1);
  };

  const handleMenuClick = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedData(row);
    setElement(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedData(null);
  };

  const handleEdit = () => {
    handleMenuClose();
    setAddData(true);
    setViewData(false);
  };

  const handleView = () => {
    handleMenuClose();
    setViewData(true);
    setAddData(false);
  };

  const handleDelete = () => {
    handleMenuClose();
    setDeleteDialog(true);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleApplyFilters = () => {
    setPage(1);
    fetchData(filtersToApply);
    setStartDate(tempFilter?.startDate);
    setEndDate(tempFilter?.endDate);
    setSelectedFields({
    });
    setIsFiltersApplied(true);
    handleAppliedFilters();
    setDrawerFilterOpen(false);
  };

  const handleResetFilters = async () => {
    setTempFilter({
      startDate: null,
      endDate: null,
    });
    setStartDate(null);
    setEndDate(null);
    setSelectedFields({
    });
    setPage(1);
    await fetchData({});
    setErrors({});
    setAppliedFilters({});
    setIsFiltersApplied(false);
    setDrawerFilterOpen(false);
  };

  const handleAppliedFilters = () => {
    const isDateApplied =
      tempFilter?.startDate || tempFilter?.endDate ? true : false;

    setIsFiltersApplied(isDateApplied );

    setAppliedFilters((prev) => ({
      ...prev,
      Date: isDateApplied,
    }));
  };

  const handleSort = (column, direction) => {
    setSortConfig({ key: column, direction });
  };

  const handleFilterDrawer = () => {
    setTempFilter({
      startDate: startDate,
      endDate: endDate,
    });
    setDrawerFilterOpen(false);
  };

  const columnHeadings = [
    { fieldName: "createdAt", title: "Date" },
    { fieldName: "assetId", title: "Asset ID" },
    // { fieldName: "typeOfAsset", title: "Type of Asset" },
    { fieldName: "leaseStartDate", title: "Lease start" },
    { fieldName: "leaseEndDate", title: "Lease end" },
    { fieldName: "assetSize", title: "Size" },
    // { fieldName: "leasedType", title: "Type" },
  ];

  const renderSelectedFilter = () => {
    switch (selectedFilter) {
      case "Date":
        return (
          <DateRangePickerComponent
            startDate={tempFilter?.startDate}
            endDate={tempFilter?.endDate}
            setStartDate={(date) =>
              setTempFilter((prev) => ({ ...prev, startDate: date }))
            }
            setEndDate={(date) =>
              setTempFilter((prev) => ({ ...prev, endDate: date }))
            }
            setErrors={setErrors}
            errors={errors.Date}
          />
        );
    
      default:
        return null;
    }
  };

  const tableContainerRef = useRef(null);
  const [isShortContent, setIsShortContent] = useState(false);

  useEffect(() => {
    const container = tableContainerRef.current;
    if (container) {
      const tableHeight = container.scrollHeight;
      const screenHeight = window.innerHeight;
      setIsShortContent(tableHeight < screenHeight / 1.5);
    }
  }, [data]);

  const getCellFieldData = (item, el) => {
    if (el.fieldName === "assetSize") {
      const assetSizeValue =
        item?.assetSize && typeof item.assetSize === "object"
          ? item.assetSize?.value
          : item?.assetSize;
      const assetSizeUnit =
        item?.assetSize && typeof item.assetSize === "object"
          ? item.assetSize?.unit || item?.assetSizeUnit
          : item?.assetSizeUnit;
      const u = assetSizeUnit ? ` ${assetSizeUnit}` : "";
      return `${assetSizeValue ?? ""}${u}`.trim() || "N/A";
    }
    if (el.fieldName === "leasedType" && item.leasedType) {
      const t = String(item.leasedType);
      return t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();
    }
    const v = item[el.fieldName];
    if (typeof v === "object" && v !== null && !Array.isArray(v)) return v;
    return v;
  };

  const getDateFieldForColumn = (item, el) => {
    if (el.fieldName === "createdAt") return item.createdAt;
    if (el.fieldName === "leaseStartDate") return item.leaseStartDate;
    if (el.fieldName === "leaseEndDate") return item.leaseEndDate;
    return false;
  };

  return (
    <MainDashboardLayout sidebar={<MastersSidebar />}>
      <Unauthorized requiredModule="Setup | Master">
        <Box
          sx={{
            backgroundColor: "#ededed",
            width: "100%",
          }}
        >
          <MessagePopup {...{ snackOpen, setsnackOpen }} />
          <AddLeasedAssets
            id={element ? element._id : ""}
            addData={addData}
            setAddData={setAddData}
            data={data}
            element={element}
            addUrl={`${api}/master/leasedAssets`}
            updateUrl={`${api}/master/leasedAssets`}
            fetchData={fetchData}
            setsnackOpen={setsnackOpen}
            formInputData={formInputData}
            leasedType={leasedType}
            leaseEndStrictlyAfterStart
          />
          <ViewLeasedAssets
            id={element ? element._id : ""}
            data={element}
            viewData={viewData}
            setViewData={setViewData}
            inputData={viewInputData}
            handleEdit={handleEdit}
          />
          {deleteDialog && (
            <DeleteDialog
              fetchData={updatePage}
              {...{
                deleteDialog,
                setDeleteDialog,
                id: element._id,
                url: `${api}/master/leasedAssets`,
                method: "delete",
                setsnackOpen,
              }}
            />
          )}
          {drawerFilterOpen && (
            <FilterDrawer
              {...{
                renderSelectedFilter,
                drawerFilterOpen,
                setDrawerFilterOpen,
                handleApplyFilters,
                handleFilterDrawer,
                setSelectedFilter,
                handleResetFilters,
                selectedFilter,
                filters,
                appliedFilters,
                selectedFields,
                setSelectedFields,
              }}
            />
          )}
          <TopMenu
            setData={setElement}
            setAddData={setAddData}
            {...{
              heading: "Scope 3",
              setDrawerFilterOpen,
              filterApplied: isFiltersApplied,
            }}
          />
          <Grid container sx={{ margin: "0 0 1rem 0", px: 0 }}>
            <Grid item xs={12} sx={{ display: "flex", gap: "1rem" }}>
              <Button
                variant={leasedTypeButton === "Upstream" ? "contained" : "outlined"}
                onClick={() => handleLeasedTypeToggle("Upstream")}
                sx={{
                  backgroundColor:
                    leasedTypeButton === "Upstream" ? "#1C486B" : "#ededed",
                  color: leasedTypeButton === "Upstream" ? "#ededed" : "#1C486B",
                  borderColor: "#1C486B",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor:
                      leasedTypeButton === "Upstream" ? "#123A55" : "#F0F8FF",
                  },
                }}
              >
                Upstream
              </Button>
              <Button
                variant={
                  leasedTypeButton === "Downstream" ? "contained" : "outlined"
                }
                onClick={() => handleLeasedTypeToggle("Downstream")}
                sx={{
                  backgroundColor:
                    leasedTypeButton === "Downstream" ? "#1C486B" : "#ededed",
                  color:
                    leasedTypeButton === "Downstream" ? "#ededed" : "#1C486B",
                  borderColor: "#1C486B",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor:
                      leasedTypeButton === "Downstream" ? "#123A55" : "#F0F8FF",
                  },
                }}
              >
                Downstream
              </Button>
            </Grid>
          </Grid>
          <TableContainer
            component={Paper}
            ref={tableContainerRef}
            sx={{ maxWidth: "100%" }}
          >
            <Box>
              <TableHeading heading={"Leased assets"} />
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns:
                    "12% 17% 17% 20% 25% 8%",
                  borderBottom: "1px solid #ddd",
                  borderTop: "1px solid #ddd",
                }}
              >
                {columnHeadings?.map((el) => (
                  <ColumnHeadings
                    key={el.fieldName}
                    {...{
                      title: el.title,
                      key: el.title,
                      handleSort,
                      field: el.fieldName,
                      sortConfig,
                    }}
                  />
                ))}
                <Typography
                  sx={{
                    fontWeight: "bold",
                    padding: "8px 12px",
                    textAlign: "center",
                  }}
                >
                  Action
                </Typography>
              </Box>
              {data.length === 0 ? (
                <NoDataFound />
              ) : (
                data?.map((item) => (
                  <Box
                    key={item._id}
                    sx={{
                      display: "grid",
                      gridTemplateColumns:
                        "12% 17% 17% 20% 25% 8%",
                      borderBottom: "1px solid #ddd",
                    }}
                  >
                    {columnHeadings?.map((el) => (
                      <TableContent
                        key={el.fieldName}
                        {...{
                          fieldData: getCellFieldData(item, el),
                          dateField: getDateFieldForColumn(item, el),
                        }}
                      />
                    ))}
                    <MenuIcon {...{ handleMenuClick, field: item }} />
                  </Box>
                ))
              )}
            </Box>
          </TableContainer>
          {totalPages > 1 && (
            <PaginationBox
              isShortContent={isShortContent}
              {...{ totalPages, page, handleChangePage, StyledPaginationItem }}
            />
          )}
          <MenuActions
            {...{
              id: element?._id,
              handleView,
              handleEdit,
              handleDelete,
              anchorEl,
              handleMenuClose,
            }}
          />
        </Box>
      </Unauthorized>
    </MainDashboardLayout>
  );
};

export default LeasedAssets;
